'use client';

import { createElement, useState } from 'react';

import Image from 'next/image';

import ReactPlayer from 'react-player/lazy';

import {
  Badge,
  Button,
  CommentCard,
  CommentCardContent,
  CommentCardHeader,
  CommentForm,
  Empty,
  Pagination,
  PlayIcon,
  ScrollArea,
  StarRating,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TypographyDescription,
  TypographyH5,
  TypographyH6,
  TypographyMuted,
  TypographyStrong,
} from '@/components';
import { useCheckAuthValue, useProductValue } from '@/context';
import { generateFileUrl, generateThumbnailUrl } from '@/utils';

const ProductDescription = () => {
  const { description } = useProductValue();

  if (!description) {
    return (
      <Empty
        heading='متاسفانه هنوز بررسی تخصصی برای این محصول ثبت نشده است.'
        paragraph='به نظر می رسد در حال حاضر هیچ داده ای برای نمایش وجود ندارد.'
      />
    );
  }

  return <TypographyDescription description={description} />;
};

const ProductVideos = () => {
  const { pictures = [] } = useProductValue();

  const videos = pictures.filter(({ type }) => type === 'Video');

  const [selectedVideoId, setSelectedVideoId] = useState(
    () => videos.at(0)?.id,
  );

  const showFooter = videos.length > 1;

  return (
    <div className='mx-auto max-w-4xl space-y-4'>
      <figure className='relative max-h-dvh w-full [&_video]:rounded-lg'>
        {selectedVideoId ? (
          <ReactPlayer
            controls
            url={generateFileUrl('Product', selectedVideoId, 1280, 720)}
            width='100%'
            height='100%'
          />
        ) : null}
      </figure>
      {showFooter ? (
        <ScrollArea orientation='horizontal' className='pb-2'>
          <div className='flex items-center gap-4'>
            {videos.map(({ id, title }) => {
              const isActive = id === selectedVideoId;

              return (
                <Button
                  key={id}
                  variant='outline'
                  data-active={isActive}
                  className='group relative size-24 p-2 data-[active=true]:border-primary'
                  onClick={() => setSelectedVideoId(id)}
                >
                  <Image
                    src={generateThumbnailUrl('Product', id)}
                    alt={title}
                    width={95}
                    height={95}
                    className='size-full rounded-sm'
                  />
                  <figure className='absolute rounded-full bg-foreground/20 p-3 group-data-[active=true]:hidden'>
                    <PlayIcon
                      weight='fill'
                      className='size-6 text-background'
                    />
                  </figure>
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      ) : null}
    </div>
  );
};

const ProductTabsAttributes = () => {
  const { name, baseAttributes } = useProductValue();

  return (
    <div className='flex gap-8 max-lg:flex-col'>
      <div className='basis-1/5'>
        <TypographyH5 className='flex flex-col gap-1.5'>
          <span className='font-bold'>ویژگی های</span>
          <TypographyMuted>{name}</TypographyMuted>
        </TypographyH5>
      </div>

      <div className='flex flex-1 flex-col'>
        <TypographyStrong className='mb-2 font-bold'>مشخصات</TypographyStrong>
        <div className='flex flex-col gap-2'>
          {baseAttributes
            ?.filter(({ selectedValues }) => selectedValues.length > 0)
            .map(({ id, name, selectedValues }) => (
              <div key={id} className='flex gap-2 lg:gap-4'>
                <div className='flex basis-1/3 items-center gap-1 rounded-sm bg-muted px-4 py-2.5 lg:basis-1/4'>
                  <TypographyMuted>{name}</TypographyMuted>
                </div>

                <div className='flex flex-1 flex-wrap items-center gap-1 rounded-sm border border-border p-2.5'>
                  {selectedValues.map(value => (
                    <Badge key={value} variant='muted'>
                      {value}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const ProductComments = () => {
  const { comments, calculatedComments } = useProductValue();
  const { success } = useCheckAuthValue();

  const { data = [] } = comments ?? {};
  const { total, rate } = calculatedComments ?? {};

  return (
    <div className='flex gap-8 max-lg:flex-col'>
      <div className='basis-1/4'>
        <div className='flex flex-col gap-2.5'>
          {total ? (
            <>
              <TypographyH6>امتیاز کاربران</TypographyH6>
              <div className='flex items-center gap-2'>
                <StarRating value={rate || 0} />
                <div className='font-light'>
                  (<span>5 / </span>
                  <span className='font-bold'>{rate}</span>)
                </div>
              </div>
            </>
          ) : null}

          <TypographyMuted>
            اگر این محصول را از کاشی لند خریداری کرده اید و یا تجربه استفاده از
            آن را دارید، میتوانید با زدن روی دکمه زیر نظر خود را ثبت نمایید
          </TypographyMuted>

          <CommentForm isUser={success} />
        </div>
      </div>

      <div className='flex flex-1 flex-col gap-4'>
        {total ? (
          <>
            {data.map(comment => {
              const { id } = comment;
              return (
                <CommentCard key={id} {...comment}>
                  <CommentCardHeader />
                  <CommentCardContent />
                </CommentCard>
              );
            })}
            <Pagination total={total} />
          </>
        ) : (
          <Empty
            heading='هنوز دیدگاهی برای این محصول ثبت نشده است.'
            paragraph='به نظر می رسد در حال حاضر هیچ داده ای برای نمایش وجود ندارد.'
          />
        )}
      </div>
    </div>
  );
};

const ProductTabs = () => {
  const { baseAttributes, description, pictures = [] } = useProductValue();

  const videos = pictures.filter(({ type }) => type === 'Video');

  const tabs = [
    {
      id: 'DESCRIPTION',
      value: 'بررسی تخصصی',
      content: ProductDescription,
      disabled: !description,
    },
    {
      id: 'VIDEOS',
      value: 'ویدیوی تخصصی محصول',
      content: ProductVideos,
      disabled: videos.length === 0,
    },
    {
      id: 'DETAILS',
      value: 'مشخصات',
      content: ProductTabsAttributes,
      disabled: baseAttributes?.length === 0,
    },
    {
      id: 'COMMENTS',
      value: 'دیدگاه کاربران',
      content: ProductComments,
      disabled: false,
    },
  ];

  const filteredTabs = tabs.filter(({ disabled }) => !disabled);
  const firstTabId = filteredTabs.at(0)?.id;

  return (
    <Tabs defaultValue={firstTabId} className='space-y-6'>
      <div className='lg:container'>
        <ScrollArea orientation='horizontal'>
          <TabsList className='w-full justify-evenly gap-4 border-b pb-2 max-lg:px-4 md:justify-center'>
            {filteredTabs.map(({ id, value }) => (
              <TabsTrigger key={id} value={id} className='p-0'>
                {value}
              </TabsTrigger>
            ))}
          </TabsList>
        </ScrollArea>
      </div>

      <div className='container'>
        {filteredTabs.map(({ id, content }) => (
          <TabsContent key={id} value={id}>
            {createElement(content)}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
};

export { ProductTabs };
